import {MaskControl} from '@wandb/weave/common/components/MediaCard';
import {MaskOptions} from '@wandb/weave/common/types/media';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {
  DEFAULT_ALL_MASK_CONTROL,
  DEFAULT_CLASS_MASK_CONTROL,
} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import Color from 'color';
import produce from 'immer';
import {find, includes, isNumber, setWith} from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {segmentationMaskColor} from '../../../../../../util/media';
import {defaultLayout} from '../../../../../PanelMediaBrowser/utils/maskControlUtils';
import {OverlaysTable} from '../../common/OverlaysTable';
import {OverlaysTableRow} from '../../common/OverlaysTableRow';
import {useMediaSettingsDrawerContext} from '../../MediaSettingsDrawerContextProvider';

type MaskClassesProps = {
  maskKey: string;
  mediaKey: string;
  maskSearchRegex: RegExp | null;
  getMaskControl: (configPath: Array<string>) => MaskControl | undefined;
};

export const MaskClasses = ({
  maskKey,
  mediaKey,
  maskSearchRegex,
  getMaskControl,
}: MaskClassesProps) => {
  const {allMasks, config, updateConfig} = useMediaSettingsDrawerContext();
  const {tileLayout} = config;
  const classDict = allMasks[mediaKey][maskKey].value;

  const update = useCallback(
    (newControl: Partial<MaskControl>, configPath: Array<string>) => {
      const maskControl =
        getMaskControl(configPath) ??
        (configPath[configPath.length - 1] === 'all'
          ? DEFAULT_ALL_MASK_CONTROL
          : DEFAULT_CLASS_MASK_CONTROL);
      const newConfig = produce(config.segmentationMaskConfig ?? {}, draft => {
        if (
          configPath[configPath.length - 1] === 'all' &&
          draft.toggles?.[mediaKey]?.[maskKey]
        ) {
          // clear all existing toggles. This is so that we will properly fall back to the
          // parent mask control below.
          delete draft.toggles[mediaKey][maskKey];
        }
        return setWith(
          draft,
          configPath,
          {...maskControl, ...newControl},
          Object
        );
      });
      updateConfig({segmentationMaskConfig: newConfig});
    },
    [
      config.segmentationMaskConfig,
      getMaskControl,
      updateConfig,
      mediaKey,
      maskKey,
    ]
  );

  const allConfigPath = useMemo(
    () => ['toggles', mediaKey, 'all', 'all'],
    [mediaKey]
  );
  const allMaskControl = useMemo(
    () => getMaskControl(allConfigPath),
    [getMaskControl, allConfigPath]
  );
  const parentConfigPath = useMemo(
    () => ['toggles', mediaKey, maskKey, 'all'],
    [mediaKey, maskKey]
  );
  const parentMaskControl = useMemo(
    () => getMaskControl(parentConfigPath),
    [getMaskControl, parentConfigPath]
  );

  const isImageShownForMask = useMemo(() => {
    const layout = tileLayout?.[mediaKey];

    if (layout == null) {
      return true;
    }

    const mo = find(layout.maskOptions, (v: MaskOptions) =>
      includes(v.maskKeys, maskKey)
    );

    return mo?.showImage ?? true;
  }, [tileLayout, mediaKey, maskKey]);

  const toggleImageForMask = useCallback(() => {
    const allLayouts = tileLayout ?? {};
    const isShown = isImageShownForMask;

    const newLayout = produce(allLayouts, draft => {
      // We need to set a default layout to perform updates
      if (draft[mediaKey] == null) {
        const allMaskKeys = Object.keys(allMasks[mediaKey]);
        draft[mediaKey] = defaultLayout(allMaskKeys);
      }
      const layout = draft[mediaKey];
      for (let i = 0; i < layout.maskOptions.length; i++) {
        const maskKeys = layout.maskOptions[i].maskKeys;

        if (includes(maskKeys, maskKey)) {
          layout.maskOptions[i].showImage = !isShown;
        }
      }
    });
    updateConfig({tileLayout: newLayout});
  }, [
    tileLayout,
    allMasks,
    updateConfig,
    isImageShownForMask,
    mediaKey,
    maskKey,
  ]);

  // This defines the hierarchy of mask controls. The child mask controls can override
  // the parent mask controls. This is so that the user can hide all masks, but then enable
  // a specific mask.
  const isAllMaskDisabled = allMaskControl?.disabled ?? false;
  const isParentMaskDisabled = parentMaskControl?.disabled ?? isAllMaskDisabled;
  const getIsMaskDisabled = (maskControl: MaskControl | undefined) =>
    maskControl?.disabled ?? isParentMaskDisabled;

  return (
    <OverlaysTable
      isAllVisible={isParentMaskDisabled !== true}
      setIsAllVisible={() =>
        update({disabled: !parentMaskControl?.disabled}, parentConfigPath)
      }
      title={maskKey}
      rightContent={
        <div className="flex items-center">
          <Button
            data-test={`visibility-toggle-images-${mediaKey}-${maskKey}`}
            variant="ghost"
            icon={isImageShownForMask ? 'visible' : 'not-visible'}
            onClick={toggleImageForMask}
          />
          <div className="ml-4 font-semibold text-moon-600">Images</div>
        </div>
      }>
      {Object.entries(classDict)
        .filter(([_, name]) =>
          maskSearchRegex ? name.match(maskSearchRegex) : true
        )
        .map(([k, name]) => {
          const defaultMaskColor = segmentationMaskColor(Number(k));
          const configPath = ['toggles', mediaKey, maskKey, k];
          const maskControl = getMaskControl(configPath);

          let maskColor = maskControl?.rgb ?? defaultMaskColor;
          if (maskColor.some(color => !isNumber(color))) {
            maskColor = defaultMaskColor;
          }
          const onUpdateColor = (color: string) => {
            const chosenColor = Color(color);
            return update(
              {
                opacity: chosenColor.alpha(),
                rgb: [
                  chosenColor.red(),
                  chosenColor.green(),
                  chosenColor.blue(),
                ],
              },
              configPath
            );
          };
          return (
            <OverlaysTableRow
              key={k}
              title={name}
              data-test={`mask-class-${name}`}
              isVisible={!getIsMaskDisabled(maskControl)}
              setIsVisible={() =>
                update({disabled: !getIsMaskDisabled(maskControl)}, configPath)
              }
              color={maskColor}
              setColor={onUpdateColor}
            />
          );
        })}
    </OverlaysTable>
  );
};
